/* Copyright (c) 2017 Backpack Health LLC */
/* eslint-disable no-alert */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import './SharePDFButton.styles.scss';

import ShareCardService from '../../../../apiServices/ShareCardService';
import { OverlayBlur } from '../../../redux/ducks/systemModal';

import { Caption } from '../../../components/Text';
import IconSharePDF from '../../../components/icons/IconSharePDF';
import ShareCardPdfPreview from './ShareCardPdfPreview';
import MobileShareCardPdfViewer from './MobileShareCardPdfViewer';
import ShareCardIncludeUrl from './ShareCardIncludeUrl';
import AppUtils from '../../../../utilities/AppUtils';
import { isMobileApplication } from '../../../../utilities/MobileUtil';
import { useUIState } from '../../../hooks/useUIState';
import { useActiveUser } from '../../../hooks/useUser';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useActiveProfile } from '../../../hooks/useProfile';

const SharePDFButton = (props) => {
  const { shareCard } = props;
  const targetLanguageCode = props.languageCode;

  const [{ activeProfileId }, uiActions] = useUIState();
  const [{ preferredLanguageCode }] = useActiveUser();
  const [, profileActions] = useActiveProfile();
  const [, scActions] = useShareCardForActiveProfile(shareCard.id);
  const profile = profileActions.instantiateProfile();
  const isDependent = profile.isDependent();

  const downloadPdf = (shouldIncludeUrl) => {
    const isUrlIncluded = shareCard.shareCardURL.isIncluded();

    const shouldIncludeUrlOnPdf = isUrlIncluded && shouldIncludeUrl;
    const url = ShareCardService.getPdfUrl(
      activeProfileId,
      shareCard.data.id,
      preferredLanguageCode,
      shouldIncludeUrlOnPdf,
    );

    window.open(url);
  };

  const showModal = (content, options) => {
    uiActions.pushSystemModal(content, options);
  };
  const dismissModal = () => {
    uiActions.popSystemModal();
  };

  return AppUtils.isIosDevice() || AppUtils.isIE11()
    ? (
      <span
        className="share-pdf-button"
        onClick={() => {
          if (isMobileApplication()) {
            downloadPdf(true);
            return;
          }

          const isUrlIncluded = shareCard.shareCardURL.isIncluded();

          if (isUrlIncluded) {
            showModal(
              <ShareCardIncludeUrl
                onClickYes={() => {
                  dismissModal();
                  downloadPdf(true);
                }}
                onClickNo={() => {
                  dismissModal();
                  downloadPdf(false);
                }}
                onClose={() => {
                  dismissModal();
                }}
              />,
              {
                blurSize: OverlayBlur.inherit,
              },
            );
          } else {
            downloadPdf(false);
          }
        }}
      >
        <div className="share-pdf-icon">
          <IconSharePDF color={'rgb(0,0,0)'} />
        </div>
        <div className="share-pdf-title">
          <Caption>
            <FormattedMessage id="share.sharePdf.button.label" />
          </Caption>
        </div>
      </span>
    ) : (
      <EmbeddedPdfViewer
        profileId={activeProfileId}
        sharePdf={(includeUrlOnPdf) => {
          scActions.sharePdf(preferredLanguageCode, isDependent, shareCard.data, targetLanguageCode, includeUrlOnPdf);
        }}
        shareCard={shareCard}
        isDependent={isDependent}
        languageCode={targetLanguageCode}
        showModal={showModal}
        dismissModal={dismissModal}
      />
    );
};

interface Props {
  profileId: number;
  shareCard: any;
  languageCode: string;
  sharePdf(d): void;
  isDependent: boolean;
  showModal(c, o): void;
  dismissModal(): void;
}
class EmbeddedPdfViewer extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
      url: ShareCardService.getPdfUrl(
        this.props.profileId,
        this.props.shareCard.data.id,
        this.props.languageCode,
        true,
      ),
    };
  }

  downloadPdf(shouldIncludeUrl) {
    const isUrlIncluded = this.props.shareCard.shareCardURL.isIncluded();

    const shouldIncludeUrlOnPdf = isUrlIncluded && shouldIncludeUrl;

    if (!isMobileApplication()) {
      this.props.sharePdf(shouldIncludeUrlOnPdf);
    }

    const url = ShareCardService.getPdfUrl(
      this.props.profileId,
      this.props.shareCard.data.id,
      this.props.languageCode,
      shouldIncludeUrlOnPdf,
    );

    this.setState({
      isVisible: true,
      shouldIncludeUrlOnPdf,
      url,
    });

    isMobileApplication()
    ? this.props.showModal(
      <MobileShareCardPdfViewer
        profileId={this.props.profileId}
        initialUrl={url}
        shareCard={this.props.shareCard}
        languageCode={this.props.languageCode}
        onDownload={(includeUrl) => this.props.sharePdf(includeUrl)}
        onShare={(includeUrl) => this.props.sharePdf(includeUrl)}
        close={() => {
          this.props.dismissModal();
        }}
      />,
      {
        isBackgroundClickDisabled: false,
      },
    )
    : this.props.showModal(
      <ShareCardPdfPreview previewUrl={url} />,
      {
        isBackgroundClickDisabled: false,
        blurSize: OverlayBlur.inherit,
      },
    );
  }

  handleSharePDFButtonClick = () => {
    if (isMobileApplication()) {
      this.downloadPdf(true);
      return;
    }

    const isUrlIncluded = this.props.shareCard.shareCardURL.isIncluded();

    if (isUrlIncluded) {
      this.props.showModal(
        <ShareCardIncludeUrl
          onClickYes={this.handleIncludeUrl}
          onClickNo={this.handleDontIncludeUrl}
          onClose={() => { this.props.dismissModal() }}
        />, {
          blurSize: OverlayBlur.inherit,
        },
      );
    } else {
      this.downloadPdf(false);
    }
  }

  handleIncludeUrl = () => {
    this.props.dismissModal();
    this.downloadPdf(true);
  }

  handleDontIncludeUrl = () => {
    this.props.dismissModal();
    this.downloadPdf(false);
  }

  render() {
    return (
      <div>
        <div className="share-pdf-button" onClick={this.handleSharePDFButtonClick}>
          <div className="share-pdf-icon">
            <IconSharePDF color={'rgb(0,0,0)'} />
          </div>
          <div className="share-pdf-title">
            <Caption>
              <FormattedMessage id="share.sharePdf.button.label" />
            </Caption>
          </div>
        </div>
      </div>
    );
  }
}

export default SharePDFButton;
